/**
 * What a traveler needs to know before an international trip: which countries
 * the flights touch, whether that makes the trip international (passport
 * required at checkout), visa flags from the traveler's nationality, and
 * passport-expiry warnings.
 *
 * Pure: the airport → country lookup and the visa table are passed in, so the
 * same code runs at checkout (lazy-loaded data), on the trip page and in the
 * server's "before you go" email. Visa data comes from the open Passport Index
 * dataset (src/data/visaRequirements.js); it's a guide, never a guarantee, so
 * every message says "may need" and points to official sources.
 */
import { COUNTRIES } from '../data/countries.js';

const COUNTRY_NAMES = new Map(COUNTRIES);

// US territories: flying there from the US is domestic (no passport for US
// citizens), and visitors follow US entry rules.
const TERRITORY_PARENT = { PR: 'US', VI: 'US', GU: 'US', MP: 'US', AS: 'US', UM: 'US' };

const TERRITORY_NAMES = {
  PR: 'Puerto Rico', VI: 'the US Virgin Islands', GU: 'Guam', MP: 'the Northern Mariana Islands', AS: 'American Samoa',
};

// Free-text nationalities customers typed before checkout had a country picker.
const NATIONALITY_ALIASES = {
  american: 'US', 'african american': 'US', 'african-american': 'US', usa: 'US', us: 'US', 'u.s.': 'US', 'u.s.a.': 'US',
  america: 'US', 'united states of america': 'US', americna: 'US', amercian: 'US', ameeican: 'US', 'aftican american': 'US',
  'puerto rican': 'US', puertorican: 'US', 'puerto ricain': 'US',
  canadian: 'CA', mexican: 'MX', mexicana: 'MX', mexicano: 'MX', jamaican: 'JM', filipino: 'PH', filipina: 'PH', piilipino: 'PH',
  chilean: 'CL', haitian: 'HT', nigerian: 'NG', australian: 'AU', swedish: 'SE', indian: 'IN', dominican: 'DO', dominicana: 'DO',
  vincentian: 'VC', trinidadian: 'TT', 'trinidad & tobago': 'TT', irish: 'IE', dutch: 'NL', british: 'GB', 'british citizen': 'GB',
  uk: 'GB', england: 'GB', 'great britain': 'GB', bangladeshi: 'BD', italian: 'IT', italiana: 'IT', italiano: 'IT', german: 'DE',
  grenadian: 'GD', syrian: 'SY', colombian: 'CO', bahamian: 'BS', guyanese: 'GY', barbadian: 'BB', bajan: 'BB', ghanaian: 'GH',
  kenyan: 'KE', ethiopian: 'ET', somali: 'SO', eritrean: 'ER', pakistani: 'PK', egyptian: 'EG', moroccan: 'MA', israeli: 'IL',
  turkish: 'TR', russian: 'RU', ukrainian: 'UA', polish: 'PL', french: 'FR', spanish: 'ES', brazilian: 'BR', peruvian: 'PE',
  ecuadorian: 'EC', venezuelan: 'VE', cuban: 'CU', salvadoran: 'SV', guatemalan: 'GT', honduran: 'HN', nicaraguan: 'NI',
  vietnamese: 'VN', korean: 'KR', japanese: 'JP', chinese: 'CN', thai: 'TH', indonesian: 'ID', 'sint maarten': 'SX',
};

const NAME_TO_CODE = new Map(COUNTRIES.map(([code, name]) => [name.toLowerCase(), code]));
NAME_TO_CODE.set('united states of america', 'US');
NAME_TO_CODE.set('turkey', 'TR');
NAME_TO_CODE.set('czech republic', 'CZ');
NAME_TO_CODE.set('ivory coast', 'CI');
NAME_TO_CODE.set('cape verde', 'CV');

/** English name for an ISO-2 code ("BS" → "Bahamas"), or the code itself. */
export function countryName(code) {
  if (!code) return '';
  return COUNTRY_NAMES.get(code) || TERRITORY_NAMES[code] || code;
}

/**
 * ISO-2 code for a stored nationality or passport country: a code, a country
 * name from our picker, or a legacy free-text value ("American"). Null when
 * it isn't a country ("White", "Asian", typos we can't place).
 */
export function normalizeCountry(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  const upper = text.toUpperCase();
  if (upper.length === 2 && (COUNTRY_NAMES.has(upper) || TERRITORY_PARENT[upper])) return upper;
  const lower = text.toLowerCase().replace(/\s+/g, ' ');
  return NAME_TO_CODE.get(lower) || NATIONALITY_ALIASES[lower] || null;
}

/** The country whose entry rules apply in a territory (Puerto Rico → US). */
export function entryCountry(code) {
  return TERRITORY_PARENT[code] || code;
}

/**
 * Airport code → ISO-2 country, from the generated airport list plus the real
 * countries of the airports it hides (src/data/hiddenAirportCountries.js,
 * "CODE:CC,..."). Never borrowed from a nearby airport: that can sit across a
 * border (YDL is in Canada, its nearest offered airport PSG in the US).
 */
export function airportCountryLookup(searchAirports = [], hiddenAirportCountries = '') {
  const byCode = new Map();
  for (const [code, , , country] of searchAirports) if (code && country) byCode.set(code, country);
  for (const pair of String(hiddenAirportCountries || '').split(',')) {
    const [code, country] = pair.split(':');
    if (code && country && !byCode.has(code)) byCode.set(code, country);
  }
  return (code) => byCode.get(String(code || '').toUpperCase()) || null;
}

function airportCode(point) {
  if (!point) return '';
  if (typeof point === 'string') return point.toUpperCase();
  return String(point.iata_code || point.code || point.id || '').toUpperCase();
}

// The airline's own country code on the segment wins when it has one; the
// airport list fills in the rest.
function airportCountry(point, countryOf) {
  const carried = point && typeof point === 'object' ? point.iata_country_code || point.country_code : null;
  if (carried && /^[A-Za-z]{2}$/.test(String(carried))) return String(carried).toUpperCase();
  return countryOf(airportCode(point)) || null;
}

function ymd(value) {
  const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

/**
 * The countries a trip touches. Each slice's last stop is a destination; the
 * stops in between are connections ("transit"). The country the trip starts in
 * is where the traveler lives or is staying, so it isn't checked for visas.
 *
 * Returns { international, startCountry, stops: [{ country, role }], lastTravelDate, unknownAirports }.
 */
export function tripCountries(flight, countryOf) {
  const slices = Array.isArray(flight?.slices) ? flight.slices : [];
  const seen = new Map();
  const all = new Set();
  const unknownAirports = [];
  let startCountry = null;
  let lastTravelDate = null;

  slices.forEach((slice) => {
    const segments = Array.isArray(slice?.segments) ? slice.segments : [];
    segments.forEach((segment, index) => {
      const origin = airportCountry(segment?.origin, countryOf);
      const destination = airportCountry(segment?.destination, countryOf);
      if (!origin && segment?.origin) unknownAirports.push(airportCode(segment.origin));
      if (!destination && segment?.destination) unknownAirports.push(airportCode(segment.destination));
      if (startCountry === null && origin) startCountry = entryCountry(origin);
      if (origin) all.add(entryCountry(origin));
      if (destination) {
        all.add(entryCountry(destination));
        const role = index === segments.length - 1 ? 'destination' : 'transit';
        const prior = seen.get(destination);
        if (!prior || (prior === 'transit' && role === 'destination')) seen.set(destination, role);
      }
      // The latest calendar date of any flight, departure or arrival: a flight
      // across the date line can land "the day before" it left, and the
      // passport has to be valid for the departure too.
      for (const date of [ymd(segment?.departing_at), ymd(segment?.arriving_at)]) {
        if (date && (!lastTravelDate || date > lastTravelDate)) lastTravelDate = date;
      }
    });
  });

  const stops = [...seen.entries()]
    .filter(([country]) => entryCountry(country) !== startCountry)
    .map(([country, role]) => ({ country, role }));
  return { international: all.size > 1, startCountry, stops, lastTravelDate, unknownAirports };
}

const tableCache = new WeakMap();
function parsedTable(table) {
  let parsed = tableCache.get(table);
  if (!parsed) {
    parsed = new Map();
    tableCache.set(table, parsed);
  }
  return parsed;
}

/** The dataset's rule for a passport entering a country: { status, days } or null. */
export function visaRule(table, passport, destination) {
  if (!table || !passport || !destination) return null;
  const parsed = parsedTable(table);
  if (!parsed.has(passport)) {
    const entries = new Map();
    for (const pair of String(table[passport] || '').split(',')) {
      const [dest, value] = pair.split(':');
      if (dest) entries.set(dest, value);
    }
    parsed.set(passport, entries);
  }
  const value = parsed.get(passport).get(entryCountry(destination));
  if (value === undefined) return null;
  if (/^\d+$/.test(value)) return { status: 'visa_free', days: Number(value) };
  const status = { vf: 'visa_free', voa: 'visa_on_arrival', eta: 'eta', ev: 'e_visa', vr: 'visa_required', na: 'no_admission' }[value];
  return status ? { status, days: null } : null;
}

const NEEDS_ACTION = new Set(['eta', 'e_visa', 'visa_required', 'no_admission']);

// Changing planes without leaving the airport usually needs no visa, so a
// connection is only flagged where airside transit still needs a document:
// the US and Canada (no airside transit; ESTA, eTA or a visa), Australia and
// New Zealand (transit visa or electronic authority).
const TRANSIT_DOCUMENT_COUNTRIES = new Set(['US', 'CA', 'AU', 'NZ']);

/**
 * Visa flags for one traveler on a trip. `needsAction` flags are the ones to
 * show prominently and email about; the rest are reassurance ("no visa needed").
 * Returns { passport, flags } — passport null when the nationality isn't known.
 */
export function travelerVisaFlags({ table, nationality, passportCountry, stops }) {
  const passport = normalizeCountry(passportCountry) || normalizeCountry(nationality);
  if (!passport) return { passport: null, flags: [] };
  const flags = [];
  for (const { country, role } of stops || []) {
    if (entryCountry(country) === entryCountry(passport)) continue;
    const rule = visaRule(table, passport, country);
    if (!rule) continue;
    if (role === 'transit' && rule.status !== 'visa_free' && !TRANSIT_DOCUMENT_COUNTRIES.has(entryCountry(country))) continue;
    flags.push({ country, role, ...rule, needsAction: NEEDS_ACTION.has(rule.status) });
  }
  return { passport, flags };
}

function addMonths(date, months) {
  const d = new Date(`${date}T00:00:00Z`);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
}

/**
 * 'expires_before_return' when the passport runs out before the last flight,
 * 'under_six_months' when it expires within 6 months of it (a common entry
 * rule), else null.
 */
export function passportExpiryIssue(expiry, lastTravelDate) {
  const exp = ymd(expiry);
  const last = ymd(lastTravelDate);
  if (!exp || !last) return null;
  if (exp < last) return 'expires_before_return';
  if (exp < addMonths(last, 6)) return 'under_six_months';
  return null;
}

/**
 * What an international trip's traveler is still missing at checkout:
 * a list of field keys (nationality, passport_country, passport_number,
 * passport_expiry) plus 'passport_expired' when the passport runs out before
 * the last flight. Empty when complete, or when the trip isn't international.
 */
export function missingTravelDocument(passenger, requirement) {
  if (!requirement?.international) return [];
  const p = passenger || {};
  const missing = [];
  if (!normalizeCountry(p.nationality)) missing.push('nationality');
  if (!normalizeCountry(p.passport_country || p.passportCountry)) missing.push('passport_country');
  if (!String(p.passport_number || p.passportNumber || '').trim()) missing.push('passport_number');
  const expiry = ymd(p.passport_expiry || p.passport_expiry_date || p.passportExpiryDate);
  if (!expiry) missing.push('passport_expiry');
  else if (passportExpiryIssue(expiry, requirement.lastTravelDate) === 'expires_before_return') missing.push('passport_expired');
  return missing;
}

/** A typed first name as it should read in a sentence ("meron" → "Meron",
 *  "DAJANAE" → "Dajanae"); mixed case ("LaShonda") is kept as typed. */
export function displayFirstName(name) {
  const text = String(name || '').trim();
  if (!text) return '';
  if (text !== text.toLowerCase() && text !== text.toUpperCase()) return text;
  return text.toLowerCase().replace(/(^|[\s'-])\p{L}/gu, (m) => m.toUpperCase());
}

/** "Ana", "Ana and Ben", "Ana, Ben and Cal". */
export function joinNames(names) {
  const list = (names || []).filter(Boolean);
  if (list.length <= 1) return list[0] || '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/** One-line customer copy for a visa flag. `who` is the traveler's first name. */
export function visaFlagMessage(flag, who) {
  const name = countryName(flag.country);
  const where = flag.role === 'transit' ? `the connection in ${name}` : name;
  const person = who || 'This traveler';
  switch (flag.status) {
    case 'e_visa':
      return `${person} may need an e-visa for ${where}. Most travelers apply online before they fly.`;
    case 'visa_required':
      return `${person} may need a visa for ${where}, arranged before travel.`;
    case 'eta':
      return `${person} may need an electronic travel authorization for ${where} (like the US ESTA or UK ETA), applied for online before departure.`;
    case 'no_admission':
      return `${person} may not be admitted to ${where} on this passport. Check before traveling.`;
    case 'visa_on_arrival':
      return `${person} can likely get a visa on arrival in ${where}.`;
    default:
      return flag.days
        ? `No visa needed for ${where} (stays up to ${flag.days} days).`
        : `No visa needed for ${where}.`;
  }
}

/** One-line customer copy for a passport-expiry issue. */
export function passportExpiryMessage(issue, who) {
  const person = who ? `${who}'s` : 'This';
  if (issue === 'expires_before_return') return `${person} passport expires before the trip ends. It needs renewing before travel.`;
  if (issue === 'under_six_months') return `${person} passport expires within 6 months of the trip. Many countries require 6 months of validity.`;
  return '';
}
